import { FolderContent } from '@/api/generatedApi';
import { useTenants } from '@/services/tenants/useTenants';
import { useGetFolders } from '@/hooks/useGetFolders';
import { buildFolderTree } from '@/utils/buildFolderTree';
import { ROOT_FOLDER_ID, getContentParentFolderId } from '@/utils/folders';
import { Box, Button, Dialog, DialogActions, DialogContent } from '@mui/material';
import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { TitleText } from '../../TitleText';
import { getAncestorIds, getDescendantIds } from '../MoveFolderModal/moveFolderUtils';
import { getContentName } from './utils';
import { useBulkMoveContents } from './useBulkMoveContents';
import { FolderSelectionContent } from './FolderSelectionContent';
import { MoveConfirmationContent } from './MoveConfirmationContent';
import { TargetFolder, toTargetFolder } from './targetFolderUtils';

export const BulkMoveContentsModal = ({
  isOpen,
  onClose,
  contents,
  targetFolder,
  selectionMode,
  onTargetSelected,
  onMoveComplete,
}: {
  isOpen: boolean;
  onClose: () => void;
  contents: FolderContent[];
  targetFolder: TargetFolder;
  selectionMode?: boolean;
  onTargetSelected?: (target?: TargetFolder) => void;
  onMoveComplete?: () => void;
}) => {
  const { getPath } = useTenants();
  const { folders, isLoading: isFoldersLoading } = useGetFolders({ enabled: isOpen && selectionMode });

  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  const isMovingToRoot = targetFolder?.id === ROOT_FOLDER_ID;
  const hasTargetSelected = targetFolder !== undefined;
  const targetFolderName = targetFolder?.name ?? 'selected folder';

  const folderTree = buildFolderTree(folders, { sort: true });

  const currentFolderId = contents[0] ? getContentParentFolderId(contents[0]) : undefined;

  const hasScrolledReference = useRef(false);
  const hasInitializedExpansion = useRef(false);

  const disabledFolderIds = new Set<string>();
  for (const content of contents) {
    if (content.contentType === 'folder') {
      disabledFolderIds.add(content.id);
      for (const id of getDescendantIds(content.id, folders)) {
        disabledFolderIds.add(id);
      }
    }
  }

  if (isOpen && selectionMode && folders.length > 0 && !hasInitializedExpansion.current) {
    hasInitializedExpansion.current = true;
    setExpandedItems(getAncestorIds(currentFolderId, folders));
  }

  const treeContainerCallbackReference = (node: HTMLDivElement | null) => {
    if (node && !hasScrolledReference.current) {
      hasScrolledReference.current = true;
      requestAnimationFrame(() => {
        const selectedElement = node.querySelector('[aria-selected="true"]');
        if (selectedElement) {
          const containerRect = node.getBoundingClientRect();
          const elementRect = selectedElement.getBoundingClientRect();
          const targetPosition = elementRect.top - containerRect.top + node.scrollTop - containerRect.height / 4;
          node.scrollTo({ top: Math.max(0, targetPosition), behavior: 'smooth' });
        }
      });
    }
  };

  const { moveStates, isProcessing, isMoveComplete, completedCount, progress, startMove, abort, reset } =
    useBulkMoveContents({ contents, onMoveComplete });

  const handleFolderSelect = (_event: React.SyntheticEvent | null, itemId: string | null) => {
    if (itemId === 'root') {
      onTargetSelected?.(toTargetFolder({ id: ROOT_FOLDER_ID }));
    } else if (itemId && !disabledFolderIds.has(itemId)) {
      const folder = folders.find(f => f.id === itemId);
      onTargetSelected?.(toTargetFolder({ id: itemId, name: folder?.name }));
    }
  };

  const handleStartMove = () => {
    if (hasTargetSelected) {
      void startMove(targetFolder?.id ?? undefined);
    }
  };

  const handleClose = () => {
    abort();
    onClose();
    reset();
    hasScrolledReference.current = false;
    hasInitializedExpansion.current = false;
    if (selectionMode && onTargetSelected) {
      onTargetSelected();
    }
  };

  const sortedContents = [...contents].sort((a, b) => {
    if (a.contentType === 'folder' && b.contentType !== 'folder') return -1;
    if (a.contentType !== 'folder' && b.contentType === 'folder') return 1;
    return getContentName(a).localeCompare(getContentName(b));
  });

  const currentIsRoot = !currentFolderId;
  const targetIsRoot = targetFolder?.id === ROOT_FOLDER_ID;
  const isSameLocation = (currentIsRoot && targetIsRoot) || targetFolder?.id === currentFolderId;

  const showFolderSelection = selectionMode && !hasTargetSelected;
  const buttonSx = { textTransform: 'none', borderRadius: '8px', px: 3 };

  return (
    <Dialog
      open={isOpen}
      onClose={isProcessing ? undefined : handleClose}
      fullWidth
      maxWidth='sm'
      slotProps={{ paper: { sx: { borderRadius: '12px' } } }}
    >
      <TitleText sx={{ padding: '24px 24px 0 24px' }}>Move Items</TitleText>
      <DialogContent sx={{ padding: '16px 24px 24px 24px' }}>
        {showFolderSelection ? (
          <FolderSelectionContent
            contents={contents}
            folderTree={folderTree}
            currentFolderId={currentFolderId}
            expandedItems={expandedItems}
            disabledFolderIds={disabledFolderIds}
            isLoading={isFoldersLoading}
            treeContainerRef={treeContainerCallbackReference}
            onSelect={handleFolderSelect}
            onExpandedItemsChange={(_event, itemIds) => setExpandedItems(itemIds)}
          />
        ) : (
          <MoveConfirmationContent
            sortedContents={sortedContents}
            moveStates={moveStates}
            targetFolderName={targetFolderName}
            isMovingToRoot={isMovingToRoot}
            isProcessing={isProcessing}
            completedCount={completedCount}
            progress={progress}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ padding: '0 24px 24px 24px' }}>
        {isMoveComplete && (
          <>
            <Button onClick={handleClose} sx={buttonSx}>
              Close
            </Button>
            <Button
              component={Link}
              to={getPath(targetFolder?.id ? `assets?folderId=${targetFolder.id}` : 'assets')}
              onClick={handleClose}
              color='primary'
              variant='contained'
              sx={buttonSx}
            >
              Go to folder
            </Button>
          </>
        )}
        {!isMoveComplete && selectionMode && hasTargetSelected && (
          <>
            <Button onClick={() => onTargetSelected?.()} disabled={isProcessing} sx={buttonSx}>
              Back
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button onClick={isProcessing ? undefined : handleClose} disabled={isProcessing} sx={buttonSx}>
              Cancel
            </Button>
            <Button
              onClick={handleStartMove}
              color='primary'
              variant='contained'
              disabled={isProcessing || isSameLocation}
              sx={buttonSx}
            >
              {isProcessing ? 'Moving...' : 'Move'}
            </Button>
          </>
        )}
        {!isMoveComplete && showFolderSelection && (
          <Button onClick={handleClose} sx={buttonSx}>
            Cancel
          </Button>
        )}
        {!isMoveComplete && !selectionMode && (
          <>
            <Button onClick={isProcessing ? undefined : handleClose} disabled={isProcessing} sx={buttonSx}>
              Cancel
            </Button>
            <Button
              onClick={handleStartMove}
              color='primary'
              variant='contained'
              disabled={isProcessing || isSameLocation}
              sx={buttonSx}
            >
              {isProcessing ? 'Moving...' : 'Move'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
